<template>
	<JBox class="s-w-full s-bg-gray-100 s-min-h-screen">
		<!-- Max width wrapper -->
		<JBox class="s-p-4 s-max-w-6xl s-m-auto s-mt-4">
			<!-- Headline -->
			<JFlex class="s-mb-1">
				<JHeadline class="s-text-2xl">{{ $t("templates.templates") }}</JHeadline>
				<JFlex>
					<JSpinner variant="indigo" type="dots" :delay="0" v-if="isLoading" class="s-mr-2 s-mt-4" />
				</JFlex>
				<JButton @click="initCreateTemplate">{{ $t("templates.create") }}</JButton>
			</JFlex>

			<!-- Filter -->
			<JCheckboxGroup variant="templates" :options="templateOptions" :value="templateWhere" @input="setFilter" />

			<JFlex class="s-items-center s-my-4">
				<JInputText v-model="searchTerm" :placeholder="$t('core.searchTemplates')" />
				<button class="s-p-1 s-appearance-none focus:s-outline-none" @click="clearSearch">
					<svg
						width="24"
						height="24"
						fill="none"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path d="M6 18L18 6M6 6l12 12"></path>
					</svg>
				</button>
			</JFlex>

			<CTable
				class="s-mt-1"
				:header="[
					{ label: $t('templates.name') },
					{ label: $t('templates.type') },
					{ label: $t('templates.logicalId') },
					{ label: $t('templates.created') },
					{ label: $t('templates.lastModified') }
				]"
				:list="computedList"
			>
				<CTableRow slot-scope="{ item }">
					<CTableCell
						class="s-px-6 s-py-4 s-whitespace-no-wrap s-border-b s-border-gray-200 s-text-sm s-leading-3 s-font-medium s-text-indigo-600"
					>
						<RouterLink :to="decideToLink(item)">
							{{ item.name }}
						</RouterLink>
						<JText class="s-max-w-sm s-text-xs s-text-gray-500 s-truncate s-leading-4">{{ item.description }}</JText>
					</CTableCell>
					<CTableCell
						class="s-px-6 s-py-4 s-whitespace-no-wrap s-border-b s-border-gray-200 s-text-sm s-leading-5 s-font-medium s-text-gray-900"
					>
						{{ item.formattedTypeName }}
					</CTableCell>
					<CTableCell
						class="s-px-6 s-py-4 s-whitespace-no-wrap s-border-b s-border-gray-200 s-text-sm s-leading-5 s-font-medium s-text-gray-900"
					>
						{{ item.logicalId }}
					</CTableCell>
					<CTableCell
						class="s-px-6 s-py-4 s-whitespace-no-wrap s-border-b s-border-gray-200 s-text-sm s-leading-5 s-font-medium s-text-gray-900"
					>
						{{ item.formattedCreatedAt }}
					</CTableCell>
					<CTableCell
						class="s-px-6 s-py-4 s-whitespace-no-wrap s-border-b s-border-gray-200 s-text-sm s-leading-5 s-font-medium s-text-gray-900"
					>
						{{ item.formattedLastModifiedAt }}
					</CTableCell>
				</CTableRow>
			</CTable>

			<CMessage />

			<Portal to="layer0">
				<CModal :isVisible="showCreateModal">
					<TemplatesCreate @cancel="showCreateModal = false" @save="afterCreating" />
				</CModal>
			</Portal>
		</JBox>
	</JBox>
</template>

<script>
import debounce from "lodash.debounce"
import TemplatesCreate from "../components/TemplatesCreate"

export default {
	components: { TemplatesCreate },

	data() {
		return {
			isLoading: false,
			templateOptions: [
				{ name: this.$t("templates.htmlToPdf"), value: "wkhtmltopdf" },
				{ name: this.$t("templates.pdfDrawing"), value: "pdfDrawing" },
				{ name: this.$t("templates.emailMjml"), value: "emailMjml" },
				// { name: this.$t("templates.clauseHtml"), value: "clauseHtml" },
				{ name: this.$t("templates.dynamic"), value: "dynamic" },
				// { name: this.$t("templates.function"), value: "function" },
				// { name: this.$t("templates.notification"), value: "notification" },
				{ name: this.$t("templates.sms"), value: "sms" }
			],
			templateWhere: null,
			showCreateModal: false,
			searchTerm: ""
		}
	},
	computed: {
		computedList() {
			return this?.$store?.state?.templates?.list?.templatesList
		},
		computedWhere() {
			return this?.$store?.state?.templates?.filter?.where
		},
		isListEmpty() {
			return this.computedList.length === 0
		}
	},
	methods: {
		async fetchTemplates() {
			this.isLoading = true
			const variables = {
				sort: { fieldName: "createdAt", type: "desc" }
			}
			if (this.computedWhere || this.searchTerm) {
				variables.where = {
					and: [
						...(this.searchTerm
							? [
									{
										or: [{ logicalId: this.searchTerm }, { name_contains: this.searchTerm }, { id: this.searchTerm }]
									}
							  ]
							: []),
						...(this.computedWhere ? [this.computedWhere] : [])
					]
				}
			}
			const { isSuccess, error } = await this.$store.dispatch("templates/fetchTemplates", variables)
			this.isLoading = false

			if (!isSuccess) {
				this.$store.dispatch("addToastMessage", {
					type: "error",
					content: { type: "message", text: error }
				})
			}
		},

		async initCreateTemplate() {
			this.showCreateModal = true
		},

		decideToLink(template) {
			const { templateView, id } = template
			return {
				name: templateView,
				params: {
					id
				}
			}
		},

		async afterCreating() {
			this.showCreateModal = false
			this.fetchTemplates()
		},

		setFilter(payload) {
			this.$store.commit("templates/setFilterWhere", {
				or: payload?.map(item => ({ type: item }))
			})
		},

		clearSearch() {
			this.searchTerm = ""
		},

		triggerFilter: debounce(function() {
			this.fetchTemplates()
		}, 400)
	},
	watch: {
		computedWhere: {
			handler() {
				this.templateWhere = this.computedWhere ? this.computedWhere?.or?.map(item => item.type) : null
				this.fetchTemplates()
			},
			deep: true,
			immediate: true
		},
		searchTerm: {
			handler() {
				this.triggerFilter()
			}
		}
	}
}
</script>
